import { useState, useEffect, useCallback } from 'react';

interface FeatureFlags {
  'monte-carlo': boolean;
  'market-intelligence': boolean;
  'ai-rent-predictor': boolean;
  'risk-analytics': boolean;
  'advanced-visualizations': boolean;
  'blockchain-integration': boolean;
  'automation-features': boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  'monte-carlo': true,
  'market-intelligence': false,
  'ai-rent-predictor': false,
  'risk-analytics': false,
  'advanced-visualizations': false,
  'blockchain-integration': false,
  'automation-features': false,
};

const STORAGE_KEY = 'experimental-features-flags';

export const useExperimentalFeatures = () => {
  const [enabledFeatures, setEnabledFeatures] = useState<FeatureFlags>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_FLAGS, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.error('Error loading feature flags:', error);
    }
    return DEFAULT_FLAGS;
  });

  // Save to localStorage whenever flags change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(enabledFeatures));
    } catch (error) {
      console.error('Error saving feature flags:', error);
    }
  }, [enabledFeatures]);

  const toggleFeature = useCallback((featureId: keyof FeatureFlags) => {
    setEnabledFeatures(prev => ({
      ...prev,
      [featureId]: !prev[featureId]
    }));
  }, []);

  const enableFeature = useCallback((featureId: keyof FeatureFlags) => {
    setEnabledFeatures(prev => ({
      ...prev,
      [featureId]: true
    }));
  }, []);

  const disableFeature = useCallback((featureId: keyof FeatureFlags) => {
    setEnabledFeatures(prev => ({
      ...prev,
      [featureId]: false
    }));
  }, []);

  const isFeatureEnabled = useCallback((featureId: keyof FeatureFlags) => {
    return enabledFeatures[featureId] || false;
  }, [enabledFeatures]);

  const resetToDefaults = useCallback(() => {
    setEnabledFeatures(DEFAULT_FLAGS);
  }, []);

  const enableAll = useCallback(() => {
    setEnabledFeatures(Object.keys(DEFAULT_FLAGS).reduce((acc, key) => {
      acc[key as keyof FeatureFlags] = true;
      return acc;
    }, {} as FeatureFlags));
  }, []);

  const disableAll = useCallback(() => {
    setEnabledFeatures(Object.keys(DEFAULT_FLAGS).reduce((acc, key) => {
      acc[key as keyof FeatureFlags] = false;
      return acc;
    }, {} as FeatureFlags));
  }, []);

  return {
    enabledFeatures,
    toggleFeature,
    enableFeature,
    disableFeature,
    isFeatureEnabled,
    resetToDefaults,
    enableAll,
    disableAll,
  };
};